import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Filter } from "lucide-react";
import client, { extractError } from "@/api";

const ENTRY_TYPES = [
  { value: "", label: "All types" },
  { value: "INITIAL_CREDIT", label: "Initial credit" },
  { value: "PURCHASE_DEBIT", label: "Purchase (debit)" },
  { value: "SALE_CREDIT", label: "Sale (credit)" },
  { value: "ADMIN_ADJUSTMENT", label: "Admin adjustment" },
  { value: "REVERSAL", label: "Reversal" },
  { value: "REFUND", label: "Refund" },
  { value: "EXPIRY", label: "Expiry" },
  { value: "SUSPENSION_HOLD", label: "Suspension hold" },
];

const TYPE_LABELS = ENTRY_TYPES.reduce((a, t) => ({ ...a, [t.value]: t.label }), {});

export default function AdminCreditLedgerPage() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [entryType, setEntryType] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    client.get("/admin/companies?page=1&page_size=100").then((r) => setCompanies(r.data.items || [])).catch(() => {});
  }, []);

  const load = async (targetPage = 1) => {
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams();
      if (entryType) params.set("entry_type", entryType);
      if (companyId) params.set("company_id", companyId);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      params.set("page", targetPage); params.set("page_size", 50);
      const res = await client.get(`/admin/credit-ledger?${params.toString()}`);
      setItems(res.data.items || []); setPage(res.data.page); setTotalPages(res.data.total_pages || 1); setTotal(res.data.total || 0);
    } catch (err) {
      setError(extractError(err, "Unable to load ledger."));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(1); /* eslint-disable-next-line */ }, [entryType, companyId]);

  return (
    <div className="ledger-page" data-testid="admin-credit-ledger-page">
      <div className="advanced-filters">
        <select value={entryType} onChange={(e) => setEntryType(e.target.value)} data-testid="ledger-type-filter">
          {ENTRY_TYPES.map((t) => <option key={t.value || "all"} value={t.value}>{t.label}</option>)}
        </select>
        <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} data-testid="ledger-company-filter">
          <option value="">All companies</option>
          {companies.map((c) => <option key={c.id} value={c.id}>{c.legal_name}</option>)}
        </select>
        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="ledger-date-from" />
        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="ledger-date-to" />
        <button type="button" className="ghost-button" data-testid="apply-ledger-filters" onClick={() => load(1)}><Filter size={12} /> Apply</button>
      </div>
      {error && <div className="form-error">{error}</div>}
      {loading ? <div className="loading-block">Loading…</div> : (
        <>
          <table className="admin-table ledger-table" data-testid="ledger-table">
            <thead>
              <tr>
                <th>Reference</th><th>Date</th><th>Company</th><th>Type</th><th>Credit</th><th>Debit</th><th>Balance</th><th>Related</th><th>Description</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={9} className="empty-row">No ledger entries.</td></tr>}
              {items.map((e) => (
                <tr key={e.id} data-testid={`ledger-row-${e.id}`}>
                  <td><code>{e.reference_id}</code></td>
                  <td><small>{new Date(e.created_at).toLocaleString()}</small></td>
                  <td>{e.company_id.slice(0, 8)}…</td>
                  <td><span className={`chip chip-${e.credit_amount > 0 ? "success" : e.debit_amount > 0 ? "warning" : "info"}`}>{TYPE_LABELS[e.entry_type] || e.entry_type}</span></td>
                  <td className="amount-cell credit">{e.credit_amount ? `+₹${e.credit_amount.toLocaleString("en-IN")}` : "—"}</td>
                  <td className="amount-cell debit">{e.debit_amount ? `−₹${e.debit_amount.toLocaleString("en-IN")}` : "—"}</td>
                  <td><strong>₹{e.balance_after.toLocaleString("en-IN")}</strong></td>
                  <td>{e.related_company_name || "—"}</td>
                  <td><small>{e.description}</small></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="pagination">
            <button type="button" className="ghost-button" data-testid="ledger-prev" onClick={() => load(page - 1)} disabled={page <= 1}><ChevronLeft size={14} /> Previous</button>
            <span>Page {page} of {totalPages} · {total} entries</span>
            <button type="button" className="ghost-button" data-testid="ledger-next" onClick={() => load(page + 1)} disabled={page >= totalPages}>Next <ChevronRight size={14} /></button>
          </div>
        </>
      )}
    </div>
  );
}
